/**
 * Execution Service
 * Handles all execution-related API calls for strategy trading.
 */

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Helper for authenticated requests
async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    
    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      console.warn("[executionService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired();
    }
    
    throw new Error(error.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

// Types
export interface Allocation {
  symbol: string;
  weight: number;
  target_value?: number;
  current_value?: number;
  drift?: number;
}

// SkippedAllocation - ticker dropped due to fractionability constraints
export interface SkippedAllocation {
  symbol: string;
  target_weight: number;
  target_value: string;
  price: string;
  reason: string;
}

// IntendedTrade from Go StrategyCalculator
export interface IntendedTrade {
  investment_id: string;
  symbol: string;
  action: "BUY" | "SELL";
  shares: string;
  target_shares: string;
  current_shares: string;
  est_price: string;
  est_value: string;
  target_weight: string;
}

// PreviewResult from Go Executor.Preview
export interface PreviewResult {
  investment_id: string;
  investment_name: string;
  portfolio_value: string;
  cash_balance: string;
  allocated_value: string;
  shadow_positions: Record<string, string>;
  target_weights: Record<string, number>;
  intended_trades: IntendedTrade[];
  skipped_allocations?: SkippedAllocation[];
  reconciliation?: ReconciliationResult;
  is_first_execution: boolean;
  fractional_enabled: boolean;
  market_open: boolean;
  next_market_open?: string;
  allocation_date?: string;
  stale_data_warning?: StaleDataWarning;
}

export interface StaleDataWarning {
  allocation_date: string;
  expected_date: string;
  message: string;
}

export interface PositionInfo {
  symbol: string;
  qty: string;
  avg_entry_price: string;
  current_price: string;
  market_value: string;
  cost_basis: string;
  unrealized_pl: string;
  unrealized_pl_pct: string;
  allocation_pct: string;
}

export interface PositionsResult {
  investment_id: string;
  account_type: string;
  portfolio_value: string;
  cash_balance: string;
  cash_pct: string;
  positions: PositionInfo[];
  total_positions: number;
  market_open: boolean;
}

export interface ReconciliationResult {
  drift_detected: boolean;
  adjustments: Array<{
    symbol: string;
    expected_shares: string;
    actual_shares: string;
    difference: string;
  }>;
}

export interface ShadowUpdate {
  investment_id: string;
  symbol: string;
  prev_shares: string;
  new_shares: string;
  delta: string;
}

export interface SubmittedOrder {
  symbol: string;
  side: string;
  shares: string;
  alpaca_id?: string;
  status: string;
  filled_qty: string;
  filled_price: string;
  error_message?: string;
}

export interface ExecutionResult {
  investment_id: string;
  investment_name: string;
  success: boolean;
  skipped: boolean;
  skip_reason?: string;
  error?: string;
  reconciliation?: ReconciliationResult;
  intended_trades: Array<{
    symbol: string;
    action: string;
    shares: string;
    dollar_amount: string;
    current_weight: string;
    target_weight: string;
  }>;
  submitted_orders: SubmittedOrder[];
  shadow_updates: ShadowUpdate[];
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  skipped_allocations?: SkippedAllocation[];
  market_open: boolean;
  is_first_execution: boolean;
}

export interface LiquidateResult {
  investment_id: string;
  positions_closed: number;
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  errors?: string[];
}

// Async execution response (202 Accepted)
export interface AsyncExecutionResponse {
  execution_run_id: string;
  status: string;
  message: string;
}

// Execution run status (polling response)
export interface ExecutionRunStatus {
  id: string;
  investment_id: string;
  run_type: "run_now" | "liquidate";
  status:
    | "pending"
    | "calculating"
    | "executing_sells"
    | "executing_buys"
    | "updating_shadow"
    | "completed"
    | "partial_failure"
    | "failed";
  strategy_name: string;
  allocated_value: string;
  total_orders: number;
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  orders_skipped: number;
  mirror_enqueued: number;
  orders?: Array<{
    symbol: string;
    side: string;
    shares: string;
    alpaca_id?: string;
    status: string;
    filled_qty: string;
    filled_price: string;
    error?: string;
  }>;
  error_message?: string;
  error_phase?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  created_at: string;
}

// ============================================
// Execution API Functions
// ============================================

/**
 * Get a preview of what trades would execute for an investment
 */
export async function previewInvestment(
  investmentId: string
): Promise<PreviewResult> {
  return authRequest<PreviewResult>(
    `/api/v1/execution/investments/${investmentId}/preview`
  );
}

/**
 * Get current Alpaca positions for an investment
 */
export async function getInvestmentPositions(
  investmentId: string
): Promise<PositionsResult> {
  return authRequest<PositionsResult>(
    `/api/v1/execution/investments/${investmentId}/positions`
  );
}

/**
 * Execute strategy trades immediately (Run Now) — async
 * Returns 202 Accepted with an execution_run_id for polling.
 */
export async function executeInvestment(
  investmentId: string,
  dryRun: boolean = false
): Promise<AsyncExecutionResponse> {
  return authRequest<AsyncExecutionResponse>(
    `/api/v1/execution/investments/${investmentId}/run-now`,
    {
      method: "POST",
      body: JSON.stringify({ dryRun }),
    }
  );
}

/**
 * Liquidate all positions for an investment (sell everything) — async
 * Returns 202 Accepted with an execution_run_id for polling.
 */
export async function liquidateInvestment(
  investmentId: string
): Promise<AsyncExecutionResponse> {
  return authRequest<AsyncExecutionResponse>(
    `/api/v1/execution/investments/${investmentId}/liquidate`,
    {
      method: "POST",
    }
  );
}

/**
 * Poll execution run status.
 * Call this every 2 seconds after receiving a 202 from executeInvestment / liquidateInvestment.
 */
export async function getExecutionRunStatus(
  runId: string
): Promise<ExecutionRunStatus> {
  return authRequest<ExecutionRunStatus>(
    `/api/v1/execution/runs/${runId}/status`
  );
}

export interface CancelResult {
  success: boolean;
  message: string;
  investment_id: string;
}

/**
 * Cancel an investment that has never been executed (return cash to portfolio)
 * Only for investments that have NOT been executed
 */
export async function cancelInvestment(
  investmentId: string
): Promise<CancelResult> {
  return authRequest<CancelResult>(
    `/api/v1/execution/investments/${investmentId}/cancel`,
    {
      method: "POST",
    }
  );
}

export interface DepositResult {
  success: boolean;
  message: string;
  investment_id: string;
  amount: string;
}

/**
 * Add funds to an active investment
 * Funds will be invested on next execution run
 */
export async function depositFunds(
  investmentId: string,
  amount: number
): Promise<DepositResult> {
  return authRequest<DepositResult>(
    `/api/v1/execution/investments/${investmentId}/deposit`,
    {
      method: "POST",
      body: JSON.stringify({ amount }),
    }
  );
}

export interface PositionSold {
  symbol: string;
  target_amount: string;
  actual_amount: string;
  status: string;
}

export interface WithdrawResult {
  investment_id: string;
  amount_requested: string;
  amount_withdrawn: string;
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  positions_sold: PositionSold[];
  errors?: string[];
}

/**
 * Withdraw funds from an active investment
 * Sells positions proportionally and returns cash to portfolio
 */
export async function withdrawFunds(
  investmentId: string,
  amount: number
): Promise<WithdrawResult> {
  return authRequest<WithdrawResult>(
    `/api/v1/execution/investments/${investmentId}/withdraw`,
    {
      method: "POST",
      body: JSON.stringify({ amount }),
    }
  );
}

export interface CashFlow {
  id: string;
  investment_id: string;
  flow_type: "initial" | "deposit" | "withdrawal" | "liquidation";
  amount: string;
  portfolio_value_before?: string;
  portfolio_value_after?: string;
  notes?: string;
  executed_at: string;
  created_at: string;
}

/**
 * Get cash flow history for an investment
 */
export async function getCashFlows(
  investmentId: string
): Promise<{ investment_id: string; cash_flows: CashFlow[]; total: number }> {
  return authRequest<{ investment_id: string; cash_flows: CashFlow[]; total: number }>(
    `/api/v1/execution/investments/${investmentId}/cash-flows`
  );
}

/**
 * Get execution logs for an investment
 */
export async function getExecutionLogs(
  investmentId: string,
  limit: number = 20,
  offset: number = 0
): Promise<{ logs: unknown[]; total: number }> {
  return authRequest<{ logs: unknown[]; total: number }>(
    `/api/v1/execution/investments/${investmentId}/logs?limit=${limit}&offset=${offset}`
  );
}

/**
 * Get performance snapshots for an investment
 */
export async function getPerformanceSnapshots(
  investmentId: string,
  limit: number = 30
): Promise<unknown[]> {
  return authRequest<unknown[]>(
    `/api/v1/execution/investments/${investmentId}/snapshots?limit=${limit}`
  );
}
